// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { API_URL } from 'Config';
import { Dispatch } from 'redux';
import { RootState } from 'Store';
import { deleteRequest, getJson, patchJson, postJson } from 'Utils/http';
import { IAvailableProjectsResponse } from 'Utils/typings/state/folder';
import { CreateFolderSuccessAction, DeleteFolderSuccessAction, SetFoldersAction, SetProjectsInFolderAction, UpdateFolderSuccessAction } from './folderListActions';

export const createNewFolderThunk = (folderName: string) => async (dispatch: Dispatch, getState: () => RootState) => {
  const state = getState();
  const { token } = state.auth;
  const url = `${API_URL}/folders`;

  const payload = {
    name: folderName,
  };

  const response = await postJson<IFolder>(url, payload, token);

  if (!response.success) {
    throw new Error('Failed to create folder');
  } else {
    dispatch(CreateFolderSuccessAction(response.data));
  }

  return response;
};

export const getAllFoldersThunk = () => async (dispatch: Dispatch, getState: () => RootState) => {
  const state = getState();
  const { token } = state.auth;
  const url = `${API_URL}/folders`;
  const response = await getJson<IFolder[]>(url, token);

  if (!response.success) {
    throw new Error('Failed to fetch folders');
  } else {
    dispatch(SetFoldersAction(response.data));
  }

  return response.data;
};

export const updateFolderThunk = (folderId: string, folderName: string) => async (dispatch: Dispatch, getState: () => RootState) => {
  const state = getState();
  const { token } = state.auth;
  const url = `${API_URL}/folders/${folderId}`;

  const payload = {
    name: folderName,
  };

  const response = await patchJson<IFolder>(url, payload, token);

  if (!response.success) {
    throw new Error('Failed to update folder');
  } else {
    dispatch(UpdateFolderSuccessAction(response.data));
  }

  return response;
};

export const deleteFolderThunk = (folderId: string) => async (dispatch: Dispatch, getState: () => RootState) => {
  const state = getState();
  const { token } = state.auth;
  const url = `${API_URL}/folders/${folderId}`;

  const response = await deleteRequest(url, token);

  if (!response.success) {
    throw new Error('Failed to delete folder');
  } else {
    dispatch(DeleteFolderSuccessAction(folderId));
  }

  return response;
};

export const moveProjectToFolderThunk = (projectId: string, folderId: string) => async (dispatch: Dispatch, getState: () => RootState) => {
  const state = getState();
  const { token } = state.auth;
  const url = `${API_URL}/folders/moveTo/${folderId}/${projectId}`;

  const response = await postJson(url, {}, token);

  if (!response.success) {
    throw new Error('Failed to move project to folder');
  } else {
    dispatch(getAllFoldersThunk() as any);
  }

  return response;
};

export const getAllProjectsInFolderThunk = (folderId: string) => async (dispatch: Dispatch, getState: () => RootState) => {
  const state = getState();
  const { token } = state.auth;
  const url = `${API_URL}/folders/${folderId}/projects`;

  const response = await getJson<any[]>(url, token);

  if (!response.success) {
    throw new Error('Failed to fetch projects in folder');
  }

  dispatch(SetProjectsInFolderAction(response.data));
};

// This is used to move multiple projects to a folder
export const moveBulkProjectsToFolderThunk = (projectIds: string[], folderId: string) => async (dispatch: Dispatch, getState: () => RootState) => {
  const state = getState();
  const { token } = state.auth;
  const url = `${API_URL}/folders/${folderId}/moveBulkProjects`;

  const response = await postJson(url, { projectIds }, token);

  if (!response.success) {
    throw new Error('Failed to move bulk projects to folder');
  }

  return response;
};

// This is used to get the projects that are not in the folder and are available to be added to the folder
export const getAvailableProjectsThunk = (folderId: string) => async (dispatch: Dispatch, getState: () => RootState) => {
  const state = getState();
  const { token } = state.auth;
  const url = `${API_URL}/folders/${folderId}/availableProjects`;

  const response = await getJson<IAvailableProjectsResponse>(url, token);

  if (!response.success) {
    throw new Error('Failed to fetch available projects');
  }

  return response.data;
};
